import { isActiveJobStatus } from './trainingJobRetry';

export type FineTuningJobProgressVariant = 'running' | 'completed' | 'failed' | 'partial';

export interface FineTuningJobProgressMetrics {
  percent: number;
  variant: FineTuningJobProgressVariant;
  show: boolean;
  label: string;
}

function readPercent(value: unknown): number | null {
  if (value == null || value === '') return null;
  const n = typeof value === 'string' ? parseFloat(value) : Number(value);
  if (!Number.isFinite(n)) return null;
  return Math.min(100, Math.max(0, n));
}

function unwrapTrainingJobRow(job: unknown): Record<string, unknown> {
  if (!job || typeof job !== 'object') return {};
  let cur = job as Record<string, unknown>;
  for (let depth = 0; depth < 3; depth += 1) {
    const inner = cur.data;
    if (!inner || typeof inner !== 'object' || Array.isArray(inner)) break;
    const record = inner as Record<string, unknown>;
    if ('id' in record || 'status' in record || 'progress_percent' in record) {
      cur = record;
    } else {
      break;
    }
  }
  return cur;
}

/** Align list rows with poll payloads when the API lags on terminal status. */
export function resolveEffectiveTrainingJobStatus(job: unknown): string {
  const row = unwrapTrainingJobRow(job);
  const status = String(row.status || '').trim().toLowerCase();
  if (
    status === 'succeeded' ||
    status === 'completed' ||
    status === 'failed' ||
    status === 'cancelled' ||
    status === 'canceled' ||
    status === 'partially_succeeded'
  ) {
    return status;
  }
  const completedAt = row.completed_at ?? row.finished_at;
  if (typeof completedAt === 'string' && completedAt.trim()) {
    return String(row.error || '').trim() ? 'failed' : 'succeeded';
  }
  const progress = readPercent(row.progress_percent);
  if (progress != null && progress >= 100) {
    return String(row.error || '').trim() ? 'failed' : 'succeeded';
  }
  return status;
}

export function enrichTrainingJobRowForDisplay(job: unknown): Record<string, unknown> {
  const row = unwrapTrainingJobRow(job);
  const effectiveStatus = resolveEffectiveTrainingJobStatus(row);
  const progress = readPercent(row.progress_percent);
  const resolvedProgress =
    effectiveStatus === 'succeeded' || effectiveStatus === 'completed'
      ? 100
      : progress ?? readPercent(row.progress) ?? 0;
  return {
    ...row,
    status: effectiveStatus || row.status,
    progress_percent: resolvedProgress,
  };
}

export function mergeTrainingJobHistoryRows(
  listRows: unknown[],
  liveById: ReadonlyMap<string, Record<string, unknown>>
): Record<string, unknown>[] {
  return (Array.isArray(listRows) ? listRows : []).map((row) => {
    const base = unwrapTrainingJobRow(row);
    const id = String(base.id || '').trim();
    const live = id ? liveById.get(id) : undefined;
    return enrichTrainingJobRowForDisplay(live ? { ...base, ...live } : base);
  });
}

export function resolveFineTuningJobProgressMetrics(
  status: string | undefined | null,
  progressPercent?: number | null,
  displayPercent?: number | null
): FineTuningJobProgressMetrics {
  const normalizedStatus = (status || '').toLowerCase().trim();
  const fromApi = readPercent(progressPercent);
  const fromDisplay = readPercent(displayPercent);
  const raw = fromDisplay ?? fromApi;

  if (normalizedStatus === 'succeeded' || normalizedStatus === 'completed') {
    return {
      percent: 100,
      variant: 'completed',
      show: true,
      label: 'Complete',
    };
  }

  if (normalizedStatus === 'partially_succeeded') {
    return {
      percent: raw ?? 100,
      variant: 'partial',
      show: true,
      label: 'Partially complete',
    };
  }

  if (normalizedStatus === 'failed') {
    return {
      percent: raw ?? 0,
      variant: 'failed',
      show: true,
      label: 'Failed',
    };
  }

  if (normalizedStatus === 'cancelled') {
    return {
      percent: raw ?? 0,
      variant: 'failed',
      show: raw != null && raw > 0,
      label: 'Cancelled',
    };
  }

  if (isActiveJobStatus(normalizedStatus)) {
    const percent = Math.min(99, raw ?? 0);
    const label =
      normalizedStatus === 'queued'
        ? 'Queued'
        : normalizedStatus === 'pending'
          ? 'Pending'
          : normalizedStatus === 'cancelling'
            ? 'Cancelling…'
            : 'Training in progress…';
    return {
      percent,
      variant: 'running',
      show: true,
      label,
    };
  }

  if (raw != null && raw > 0) {
    return {
      percent: raw,
      variant: 'running',
      show: true,
      label: `${Math.round(raw)}%`,
    };
  }

  return {
    percent: 0,
    variant: 'running',
    show: false,
    label: '',
  };
}
